import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { PromptTypes } from '@kinde/js-utils';
import { discoverAuth } from '../api/authApi';
import { trackRumAction } from '@/shared/observability/rum';
import { hashForObservability } from '@/shared/observability/redaction';
import { logger } from '@/shared/observability/frontendLogger';

const EMAIL_CONNECTION_ID = import.meta.env.VITE_KINDE_EMAIL_CONNECTION_ID;
const AUTH_INTENT_KEY = 'bquest_auth_intent';

/**
 * Reads `?autoLogin=true&email=X` from the URL on mount and kicks off the OTP
 * login flow for that email — used as a hand-off after legacy Management API
 * signups (RegisterPage, PartnerSignupPage, InviteSuccessState) so the
 * freshly-created user lands logged in without an extra click.
 *
 * Originally only `HomePage` had this logic. Now lives here as a shared hook so
 * the new `PreLoginLanding` route also handles the param — required for the
 * spec-aligned migration where `/` and `/login` both render the new landing.
 *
 * Fires at most once per mount (ref guard) and is a no-op when the params
 * are missing. SSO-only accounts fall through to the manual flow.
 */
export const useAutoLoginFromQuery = () => {
  const location = useLocation();
  const { login: kindeLogin, isLoading: kindeLoading } = useKindeAuth();
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current || kindeLoading) return;

    const query = new URLSearchParams(location.search);
    const email = query.get('email');
    const autoLogin = query.get('autoLogin') === 'true';
    if (!autoLogin || !email) return;

    triggeredRef.current = true;

    void (async () => {
      const normalizedEmail = email.trim().toLowerCase();
      try {
        const result = await discoverAuth({ email: normalizedEmail });
        // Skip when discovery fails or when the account is SSO-only — those
        // flows need user interaction in the company-login UI.
        if (!result?.allowOtp || result.authMethods.includes('sso')) return;

        let emailHash: string | undefined;
        try {
          emailHash = await hashForObservability(normalizedEmail);
          trackRumAction('login_attempt', { emailHash, method: 'otp' });
        } catch {
          trackRumAction('login_attempt', { method: 'otp' });
        }

        sessionStorage.setItem(
          AUTH_INTENT_KEY,
          JSON.stringify({
            method: 'otp',
            email: normalizedEmail,
            companySlug: result.companySlug ?? null,
            expectedCompanySlug: result.companySlug ?? null,
          }),
        );

        await kindeLogin({
          connectionId: EMAIL_CONNECTION_ID,
          loginHint: normalizedEmail,
          prompt: PromptTypes.login,
        });
      } catch (error) {
        logger.error('Auto-login from query failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    })();
  }, [kindeLoading, location.search, kindeLogin]);
};
